import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";

type FormState = {
  name: string;
  age: string;
  address: string;
  pincode: string;
  email: string;
  profile: File | string | null;
};

export default function UpdatePatientProfile() {
  const user = useAppSelector((s) => s.auth.user);

  const [form, setForm] = useState<FormState>({
    name: user?.name || "",
    age: "",
    address: "",
    pincode: "",
    email: "",
    profile: user?.profile || null,
  });

  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axiosInstance.get("/profile/patient");
        const data = res.data?.data;

        if (data) {
          setForm({
            name: data.name || "",
            age: data.age?.toString() || "",
            address: data.address || "",
            pincode: data.pincode || "",
            email: data.email || "",
            profile: data.profile || null, // CDN URL
          });
        }
      } catch (err) {
        console.error("Failed to fetch patient profile", err);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setForm((prev) => ({
      ...prev,
      profile: file, // MUST be File
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name) {
      toast.error("Name and Pincode are required");
      return;
    }

    try {
      setLoading(true);

      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("pincode", form.pincode);

      if (form.age) fd.append("age", form.age);
      if (form.address) fd.append("address", form.address);
      if (form.email) fd.append("email", form.email);

      if (form.profile instanceof File) {
        fd.append("profile", form.profile);
      }

      await axiosInstance.post("/profile/update-patient", fd);

      toast.success("Profile updated successfully!");

      const res = await axiosInstance.get("/profile/patient");
      const data = res.data?.data;

      if (data) {
        setForm({
          name: data.name || "",
          age: data.age?.toString() || "",
          address: data.address || "",
          pincode: data.pincode || "",
          email: data.email || "",
          profile: data.profile || null,
        });
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="text-center text-gray-600 p-4">
        Loading profile...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-lg">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
        Update Patient Profile
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">

        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="Name"
          className="w-full border rounded-lg px-3 py-2"
        />

        <input
          type="number"
          name="age"
          value={form.age}
          onChange={handleChange}
          placeholder="Age"
          className="w-full border rounded-lg px-3 py-2"
        />

        <input
          type="text"
          name="address"
          value={form.address}
          onChange={handleChange}
          placeholder="Address"
          className="w-full border rounded-lg px-3 py-2"
        />

        <input
          type="text"
          name="pincode"
          value={form.pincode}
          onChange={handleChange}
          placeholder="Pincode"
          className="w-full border rounded-lg px-3 py-2"
        />

        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          placeholder="Email"
          className="w-full border rounded-lg px-3 py-2"
        />

        <div>
          <label className="block text-gray-600 text-sm mb-1">
            Profile Image
          </label>

          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="w-full border rounded-lg px-3 py-2"
          />

          {form.profile && (
            <img
              src={
                form.profile instanceof File
                  ? URL.createObjectURL(form.profile)
                  : form.profile
              }
              alt="Preview"
              className="mt-3 w-24 h-24 object-cover rounded-full mx-auto border"
            />
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-indigo-600 text-white py-2 rounded-lg disabled:opacity-50"
        >
          {loading ? "Updating..." : "Update Profile"}
        </button>
      </form>
    </div>
  );
}
